import React, { useState, useEffect } from "react";
import { PublicLayout } from "../../components/PublicLayout";
import { SectionTitle } from "../../components/Decor";
import { api } from "../../lib/apiClient";
import { buildWhatsappUrl } from "../../lib/whatsapp";
import { toast } from "sonner";
import { PartyPopper, Croissant, Coffee, GlassWater, Check, MessageCircle } from "lucide-react";

export default function Services() {
  const [settings, setSettings] = useState({});
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ nombre: "", telefono: "", correo: "", event_type: "", event_date: "", location: "", guests: "", comments: "" });
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    Promise.all([
      api.get("/products"),
      api.get("/categories"),
    ]).then(([pRes, cRes]) => {
      const cats = cRes.data || [];
      const catName = (id) => cats.find((c) => c.id === id)?.name || "";
      const eventProducts = (pRes.data || []).filter((p) => {
        const c = catName(p.category_id).toLowerCase();
        return c.includes("barras para eventos") || c.includes("barra para eventos");
      });
      setServices(eventProducts.map((p) => ({
        icon: p.name.toLowerCase().includes("bebida") || p.name.toLowerCase().includes("drink") ? GlassWater : p.name.toLowerCase().includes("roles") ? Croissant : p.name.toLowerCase().includes("snack") ? Coffee : PartyPopper,
        name: p.name,
        desc: p.description || "Servicio para eventos a cotización.",
      })));
    }).catch(() => {}).finally(() => setServicesLoading(false));
  }, []);

  const toggle = (name) => setSelected((s) => s.includes(name) ? s.filter((x) => x !== name) : [...s, name]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.nombre || !form.telefono || !form.event_date) { toast.error("Completa nombre, teléfono y fecha del evento."); return; }
    if (selected.length === 0) { toast.error("Elige al menos un servicio."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/event-requests", {
        customer: { nombre: form.nombre, telefono: form.telefono, correo: form.correo, comentarios: form.comments },
        event_type: form.event_type, event_date: form.event_date, location: form.location,
        guests: parseInt(form.guests) || 0, services: selected, comments: form.comments,
      });
      setDone(data);
    } catch (e) { toast.error("No pudimos enviar tu solicitud. Intenta de nuevo."); }
    setLoading(false);
  };

  if (done) {
    const wa = buildWhatsappUrl(settings.whatsapp, `Hola Kelifa, solicité una cotización de servicios.\n\nFolio: ${done.folio}\nEvento: ${done.event_type}\nFecha: ${done.event_date}\nServicios: ${done.services.join(", ")}\n\nQuedo pendiente. Gracias.`);
    return (
      <PublicLayout>
        <div className="max-w-md mx-auto px-5 py-16 text-center">
          <div className="w-24 h-24 mx-auto rounded-full bg-salvia/20 flex items-center justify-center mb-5"><Check className="w-12 h-12 text-salvia" /></div>
          <h1 className="font-serif text-4xl text-vino">¡Gracias!</h1>
          <p className="text-cafe/70 mt-2">Recibimos tu solicitud de cotización. Te contactaremos por WhatsApp muy pronto 💛</p>
          <div className="bg-blancoCalido border border-beige rounded-[28px] p-5 mt-6 text-left shadow-pastel">
            <p className="text-sm text-cafe/60">Folio</p><p className="font-serif text-2xl text-vino">{done.folio}</p>
          </div>
          <a href={wa} target="_blank" rel="noreferrer" data-testid="servicio-whatsapp-btn" className="mt-6 inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-8 py-4 font-medium hover:scale-105 transition-transform">
            <MessageCircle className="w-5 h-5" /> Ir a WhatsApp
          </a>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10">
        <SectionTitle sub="Diseñamos una experiencia dulce para tu evento. Cuéntanos qué imaginas y te enviamos una cotización personalizada.">Servicios para eventos</SectionTitle>

        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {servicesLoading && [1, 2, 3, 4].map((n) => (
            <div key={n} className="rounded-[26px] p-5 border-2 border-beige bg-blancoCalido animate-pulse h-32" />
          ))}
          {!servicesLoading && services.length === 0 && (
            <div className="sm:col-span-2 rounded-[26px] p-6 border-2 border-beige bg-blancoCalido text-center text-cafe/60">
              Aún no hay servicios activos en la categoría “Barras para eventos”.
            </div>
          )}
          {!servicesLoading && services.map((s) => {
            const active = selected.includes(s.name);
            return (
              <button key={s.name} onClick={() => toggle(s.name)} data-testid={`servicio-${s.name}`}
                className={`text-left rounded-[26px] p-5 border-2 transition-all flex gap-4 items-start ${active ? "border-vino bg-vino/5 shadow-pastel" : "border-beige bg-blancoCalido hover:border-rosa"}`}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-vino text-crema" : "bg-beige text-vino"}`}><s.icon className="w-6 h-6" /></div>
                <div><p className="font-serif text-lg text-vino flex items-center gap-2">{s.name} {active && <Check className="w-4 h-4 text-salvia" />}</p><p className="text-sm text-cafe/60">{s.desc}</p></div>
              </button>
            );
          })}
        </div>

        <div className="bg-blancoCalido border border-beige rounded-[32px] p-6 sm:p-8 shadow-pastel space-y-4">
          <h3 className="font-serif text-2xl text-vino">Cuéntanos de tu evento</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Tu nombre *" v={form.nombre} onChange={(v) => set("nombre", v)} testid="ev-nombre" />
            <Field label="WhatsApp *" v={form.telefono} onChange={(v) => set("telefono", v)} testid="ev-telefono" />
            <Field label="Correo (opcional)" v={form.correo} onChange={(v) => set("correo", v)} testid="ev-correo" />
            <Field label="Tipo de evento" v={form.event_type} onChange={(v) => set("event_type", v)} placeholder="Cumpleaños, boda…" testid="ev-tipo" />
            <Field label="Fecha del evento *" type="date" v={form.event_date} onChange={(v) => set("event_date", v)} testid="ev-fecha" />
            <Field label="Número de personas" type="number" v={form.guests} onChange={(v) => set("guests", v)} testid="ev-personas" />
            <div className="sm:col-span-2"><Field label="Lugar del evento" v={form.location} onChange={(v) => set("location", v)} testid="ev-lugar" /></div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-vino mb-1.5 block">Comentarios</label>
              <textarea data-testid="ev-comentarios" value={form.comments} onChange={(e) => set("comments", e.target.value)} rows={3}
                className="w-full bg-crema border-2 border-beige rounded-2xl px-4 py-3 text-cafe focus:outline-none focus:border-vino transition-all" />
            </div>
          </div>
          <p className="text-sm text-cafe/50 bg-crema rounded-2xl p-3 border border-beige">Te enviaremos una cotización personalizada por WhatsApp 💌</p>
          <button data-testid="ev-enviar-btn" disabled={loading} onClick={submit}
            className="w-full bg-vino text-crema rounded-full py-4 font-medium hover:bg-vinoDark transition-all disabled:opacity-60">
            {loading ? "Enviando…" : "Solicitar cotización"}
          </button>
        </div>
      </div>
    </PublicLayout>
  );
}

const Field = ({ label, v, onChange, type = "text", placeholder = "", testid }) => (
  <div>
    <label className="text-sm font-medium text-vino mb-1.5 block">{label}</label>
    <input data-testid={testid} type={type} value={v} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-crema border-2 border-beige rounded-2xl px-4 py-3 text-cafe placeholder-rosa focus:outline-none focus:border-vino transition-all" />
  </div>
);
